'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { X, ChevronLeft, Sparkles, MapPin, Play, Dna, Image, FolderOpen, Globe, CheckCircle, ArrowRight, BookOpen, Briefcase, Wand2, User } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';

// ─── Tour Storage Key ─────────────────────────────────────────────────────────
export const TOUR_COMPLETED_KEY = 'aimaker_tour_completed';
export const TOUR_VERSION = '2.0';

export function isTourCompleted(): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const val = localStorage.getItem(TOUR_COMPLETED_KEY);
    return val === TOUR_VERSION;
  } catch {
    return false;
  }
}

export function markTourCompleted(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.setItem(TOUR_COMPLETED_KEY, TOUR_VERSION);
  } catch {}
  try {
    window.dispatchEvent(new Event('aimaker-tour-completed'));
  } catch {}
}

export function resetTour(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(TOUR_COMPLETED_KEY);
  } catch {}
}

// ─── Tour Step Definitions ────────────────────────────────────────────────────
interface TourStep {
  id: string;
  icon: React.ReactNode;
  targetSelector?: string;
  position: 'center' | 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STEPS: TourStep[] = [
  { id: 'welcome', icon: <Sparkles size={18} className="text-primary" />, position: 'center' },
  { id: 'welcome_hub', icon: <MapPin size={18} className="text-accent" />, targetSelector: '[data-tour="welcome-hub"]', position: 'right' },
  { id: 'dream_dashboard', icon: <Dna size={18} className="text-primary" />, targetSelector: '[data-tour="dream-dashboard"]', position: 'right' },
  { id: 'dream_builder', icon: <Play size={18} className="text-mint" />, targetSelector: '[data-tour="dream-builder"]', position: 'right' },
  { id: 'my_projects', icon: <FolderOpen size={18} className="text-accent" />, targetSelector: '[data-tour="my-projects"]', position: 'right' },
  { id: 'knowledge_vault', icon: <BookOpen size={18} className="text-primary" />, targetSelector: '[data-tour="knowledge-vault"]', position: 'right' },
  { id: 'business_builder', icon: <Briefcase size={18} className="text-accent" />, targetSelector: '[data-tour="business-builder"]', position: 'right' },
  { id: 'visual_studio', icon: <Image size={18} className="text-mint" />, targetSelector: '[data-tour="visual-studio"]', position: 'right' },
  { id: 'ai_tools', icon: <Wand2 size={18} className="text-primary" />, targetSelector: '[data-tour="ai-tools"]', position: 'right' },
  { id: 'resources', icon: <Globe size={18} className="text-accent" />, targetSelector: '[data-tour="resources"]', position: 'right' },
  { id: 'account', icon: <User size={18} className="text-mint" />, targetSelector: '[data-tour="account"]', position: 'right' },
  { id: 'finish', icon: <CheckCircle size={18} className="text-primary" />, position: 'center' },
];

// ─── Translations ─────────────────────────────────────────────────────────────
const TOUR_TRANSLATIONS = {
  en: {
    steps: [
      { title: 'Welcome to AIMaker Studio! 🎉', description: 'You\'re about to build your dream business with AI. Let us show you around in just a few steps — it takes less than 2 minutes.' },
      { title: 'Welcome Hub', description: 'This is your home base. Start a new dream, continue an existing project, or grow an existing business from here.' },
      { title: 'Dream Dashboard™', description: 'Your Mission Control. Track your DreamHealth score, launch readiness, milestones, and today\'s mission — all in one place.' },
      { title: 'DreamBuilder™', description: 'Enter your dream and watch AI build your complete business blueprint — brand, products, content, and launch plan.' },
      { title: 'My Projects', description: 'All your saved dreams live here. Return to any project, track progress, and continue building where you left off.' },
      { title: 'Knowledge Vault™', description: 'Upload your existing business information — documents, notes, brand assets — so AI always knows your business deeply.' },
      { title: 'Business Builder', description: 'Build your complete business plan section by section — mission, products, pricing, marketing, and launch strategy.' },
      { title: 'Visual Studio', description: 'Generate stunning images, logos, brand assets, and marketing visuals using AI. No design skills needed.' },
      { title: 'AI Tools', description: 'Access all AI-powered tools — voice generation, avatar creation, SVG studio, social media, and more.' },
      { title: 'Resource Center', description: 'Video tutorials, guides, templates, and documentation to help you get the most out of every feature.' },
      { title: 'Account & Profile', description: 'Manage your profile, subscription, language preferences, and account settings here.' },
      { title: 'You\'re Ready to Build! 🚀', description: 'Start with one dream, one project, and one next step. AIMaker Studio will guide you the rest of the way. Your success starts now.' },
    ],
    skip: 'Skip Tour',
    back: 'Back',
    next: 'Next',
    finish: 'Start Building',
    stepOf: 'Step {current} of {total}',
    dontShowAgain: "Don\'t show again",
  },
  pt: {
    steps: [
      { title: 'Bem-vindo ao AIMaker Studio! 🎉', description: 'Você está prestes a construir o negócio dos seus sonhos com IA. Deixe-nos mostrar tudo em poucos passos — leva menos de 2 minutos.' },
      { title: 'Hub de Boas-Vindas', description: 'Esta é sua base. Comece um novo sonho, continue um projeto existente ou desenvolva um negócio já existente daqui.' },
      { title: 'Dream Dashboard™', description: 'Seu Centro de Controle. Acompanhe sua pontuação DreamHealth, prontidão para lançamento, marcos e missão de hoje — tudo em um lugar.' },
      { title: 'DreamBuilder™', description: 'Digite seu sonho e veja a IA construir seu blueprint completo de negócios — marca, produtos, conteúdo e plano de lançamento.' },
      { title: 'Meus Projetos', description: 'Todos os seus sonhos salvos ficam aqui. Volte a qualquer projeto, acompanhe o progresso e continue construindo de onde parou.' },
      { title: 'Cofre de Conhecimento™', description: 'Carregue suas informações de negócios existentes — documentos, notas, ativos de marca — para que a IA sempre conheça seu negócio profundamente.' },
      { title: 'Construtor de Negócios', description: 'Construa seu plano de negócios completo seção por seção — missão, produtos, preços, marketing e estratégia de lançamento.' },
      { title: 'Estúdio Visual', description: 'Gere imagens impressionantes, logos, ativos de marca e visuais de marketing usando IA. Sem necessidade de habilidades de design.' },
      { title: 'Ferramentas de IA', description: 'Acesse todas as ferramentas com IA — geração de voz, criação de avatar, estúdio SVG, mídia social e muito mais.' },
      { title: 'Central de Recursos', description: 'Tutoriais em vídeo, guias, modelos e documentação para ajudá-lo a aproveitar ao máximo cada recurso.' },
      { title: 'Conta e Perfil', description: 'Gerencie seu perfil, assinatura, preferências de idioma e configurações de conta aqui.' },
      { title: 'Você Está Pronto para Construir! 🚀', description: 'Comece com um sonho, um projeto e um próximo passo. O AIMaker Studio vai guiá-lo pelo resto do caminho. Seu sucesso começa agora.' },
    ],
    skip: 'Pular Tour',
    back: 'Voltar',
    next: 'Próximo',
    finish: 'Começar a Construir',
    stepOf: 'Passo {current} de {total}',
    dontShowAgain: 'Não mostrar novamente',
  },
  es: {
    steps: [
      { title: '¡Bienvenido a AIMaker Studio! 🎉', description: 'Estás a punto de construir el negocio de tus sueños con IA. Déjanos mostrarte todo en pocos pasos — toma menos de 2 minutos.' },
      { title: 'Hub de Bienvenida', description: 'Esta es tu base. Comienza un nuevo sueño, continúa un proyecto existente o desarrolla un negocio ya existente desde aquí.' },
      { title: 'Dream Dashboard™', description: 'Tu Centro de Control. Rastrea tu puntuación DreamHealth, preparación para el lanzamiento, hitos y misión de hoy — todo en un lugar.' },
      { title: 'DreamBuilder™', description: 'Ingresa tu sueño y observa cómo la IA construye tu blueprint completo de negocios — marca, productos, contenido y plan de lanzamiento.' },
      { title: 'Mis Proyectos', description: 'Todos tus sueños guardados viven aquí. Regresa a cualquier proyecto, rastrea el progreso y continúa construyendo donde lo dejaste.' },
      { title: 'Bóveda de Conocimiento™', description: 'Sube tu información de negocio existente — documentos, notas, activos de marca — para que la IA siempre conozca tu negocio profundamente.' },
      { title: 'Constructor de Negocios', description: 'Construye tu plan de negocios completo sección por sección — misión, productos, precios, marketing y estrategia de lanzamiento.' },
      { title: 'Estudio Visual', description: 'Genera imágenes impresionantes, logos, activos de marca y visuales de marketing usando IA. Sin necesidad de habilidades de diseño.' },
      { title: 'Herramientas de IA', description: 'Accede a todas las herramientas con IA — generación de voz, creación de avatar, estudio SVG, redes sociales y más.' },
      { title: 'Centro de Recursos', description: 'Tutoriales en video, guías, plantillas y documentación para ayudarte a aprovechar al máximo cada función.' },
      { title: 'Cuenta y Perfil', description: 'Gestiona tu perfil, suscripción, preferencias de idioma y configuración de cuenta aquí.' },
      { title: '¡Estás Listo para Construir! 🚀', description: 'Comienza con un sueño, un proyecto y un siguiente paso. AIMaker Studio te guiará el resto del camino. Tu éxito comienza ahora.' },
    ],
    skip: 'Omitir Tour',
    back: 'Atrás',
    next: 'Siguiente',
    finish: 'Empezar a Construir',
    stepOf: 'Paso {current} de {total}',
    dontShowAgain: 'No mostrar de nuevo',
  },
};

// ─── Tooltip Position Calculator ──────────────────────────────────────────────
interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function getTargetRect(selector: string): TargetRect | null {
  if (typeof window === 'undefined') return null;
  const el = document.querySelector(selector);
  if (!el) return null;
  const rect = el.getBoundingClientRect();
  return { top: rect.top, left: rect.left, width: rect.width, height: rect.height };
}

interface TooltipStyle {
  top?: string;
  left?: string;
  transform?: string;
  bottom?: string;
  right?: string;
}

const CARD_WIDTH = 300;
const CARD_GAP = 16;

function getTooltipStyle(
  position: TourStep['position'],
  targetRect: TargetRect | null,
): { card: TooltipStyle; arrowSide: string } {
  if (!targetRect || position === 'center') {
    return {
      card: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' },
      arrowSide: 'none',
    };
  }

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1200;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 800;
  const clampLeft = (l: number) => Math.min(Math.max(l, 12), vw - CARD_WIDTH - 12);
  const clampTop = (t: number) => Math.min(Math.max(t, 12), vh - 260);

  switch (position) {
    case 'bottom':
      return {
        card: {
          top: `${Math.min(targetRect.top + targetRect.height + CARD_GAP, vh - 260)}px`,
          left: `${clampLeft(targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2)}px`,
        },
        arrowSide: 'top',
      };
    case 'top':
      return {
        card: {
          top: `${Math.max(targetRect.top - CARD_GAP, 12)}px`,
          left: `${clampLeft(targetRect.left + targetRect.width / 2 - CARD_WIDTH / 2)}px`,
          transform: 'translateY(-100%)',
        },
        arrowSide: 'bottom',
      };
    case 'right':
      return {
        card: {
          top: `${clampTop(targetRect.top + targetRect.height / 2)}px`,
          left: `${Math.min(targetRect.left + targetRect.width + CARD_GAP, vw - CARD_WIDTH - 12)}px`,
          transform: 'translateY(-50%)',
        },
        arrowSide: 'left',
      };
    case 'left':
      return {
        card: {
          top: `${clampTop(targetRect.top + targetRect.height / 2)}px`,
          left: `${Math.max(targetRect.left - CARD_WIDTH - CARD_GAP, 12)}px`,
          transform: 'translateY(-50%)',
        },
        arrowSide: 'right',
      };
    default:
      return { card: { top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }, arrowSide: 'none' };
  }
}

// ─── Animated Arrow ───────────────────────────────────────────────────────────
function TooltipArrow({ side }: { side: string }) {
  if (side === 'none') return null;

  const arrowStyles: Record<string, React.CSSProperties> = {
    top: {
      position: 'absolute',
      top: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderBottom: '8px solid rgba(15, 15, 25, 0.95)',
    },
    bottom: {
      position: 'absolute',
      bottom: '-8px',
      left: '50%',
      transform: 'translateX(-50%)',
      width: 0,
      height: 0,
      borderLeft: '8px solid transparent',
      borderRight: '8px solid transparent',
      borderTop: '8px solid rgba(15, 15, 25, 0.95)',
    },
    left: {
      position: 'absolute',
      top: '50%',
      left: '-8px',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderRight: '8px solid rgba(15, 15, 25, 0.95)',
    },
    right: {
      position: 'absolute',
      top: '50%',
      right: '-8px',
      transform: 'translateY(-50%)',
      width: 0,
      height: 0,
      borderTop: '8px solid transparent',
      borderBottom: '8px solid transparent',
      borderLeft: '8px solid rgba(15, 15, 25, 0.95)',
    },
  };

  return <div style={arrowStyles[side] ?? {}} />;
}

// ─── Pulse Highlight Ring ─────────────────────────────────────────────────────
function PulseHighlight({ targetRect }: { targetRect: TargetRect | null }) {
  if (!targetRect) return null;
  const pad = 8;
  return (
    <>
      {/* Outer pulse ring */}
      <div
        className="fixed pointer-events-none z-[9997] rounded-xl"
        style={{
          top: targetRect.top - pad - 4,
          left: targetRect.left - pad - 4,
          width: targetRect.width + (pad + 4) * 2,
          height: targetRect.height + (pad + 4) * 2,
          boxShadow: '0 0 0 2px rgba(139,92,246,0.4)',
          animation: 'tour-pulse-outer 2s ease-in-out infinite',
        }}
      />
      {/* Inner glow ring */}
      <div
        className="fixed pointer-events-none z-[9998] rounded-xl"
        style={{
          top: targetRect.top - pad,
          left: targetRect.left - pad,
          width: targetRect.width + pad * 2,
          height: targetRect.height + pad * 2,
          boxShadow: '0 0 0 2px rgba(139,92,246,0.8), 0 0 16px rgba(139,92,246,0.3), inset 0 0 8px rgba(139,92,246,0.05)',
        }}
      />
    </>
  );
}

// ─── Auto-dismiss Progress Bar ────────────────────────────────────────────────
function AutoDismissBar({ duration, onComplete }: { duration: number; onComplete: () => void }) {
  const [width, setWidth] = useState(100);
  const startRef = useRef<number | null>(null);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    startRef.current = null;
    setWidth(100);

    const animate = (ts: number) => {
      if (startRef.current === null) startRef.current = ts;
      const elapsed = ts - startRef.current;
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setWidth(remaining);
      if (remaining > 0) {
        rafRef.current = requestAnimationFrame(animate);
      } else {
        onComplete();
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [duration, onComplete]);

  return (
    <div className="h-0.5 bg-white/10 rounded-full overflow-hidden mt-3">
      <div
        className="h-full bg-gradient-to-r from-primary to-accent rounded-full transition-none"
        style={{ width: `${width}%` }}
      />
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
interface OnboardingTourProps {
  onComplete?: () => void;
}

export default function OnboardingTour({ onComplete }: OnboardingTourProps) {
  const { language } = useLanguage();
  const [active, setActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);
  const autoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const tr = TOUR_TRANSLATIONS[language as keyof typeof TOUR_TRANSLATIONS] ?? TOUR_TRANSLATIONS.en;
  const step = TOUR_STEPS[currentStep];
  const stepData = tr.steps[currentStep];
  const total = TOUR_STEPS.length;

  const completeTour = useCallback(() => {
    setVisible(false);
    setTimeout(() => {
      setActive(false);
      markTourCompleted();
      onComplete?.();
    }, 300);
  }, [onComplete]);

  const goNext = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    if (currentStep < total - 1) {
      setVisible(false);
      setTimeout(() => {
        setCurrentStep(s => s + 1);
        setVisible(true);
      }, 200);
    } else {
      completeTour();
    }
  }, [currentStep, total, completeTour]);

  const goBack = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    if (currentStep > 0) {
      setVisible(false);
      setTimeout(() => {
        setCurrentStep(s => s - 1);
        setVisible(true);
      }, 200);
    }
  }, [currentStep]);

  const skipTour = useCallback(() => {
    if (autoTimerRef.current) clearTimeout(autoTimerRef.current);
    completeTour();
  }, [completeTour]);

  const handleAutoDismiss = useCallback(() => {
    goNext();
  }, [goNext]);

  useEffect(() => {
    setMounted(true);
    if (!isTourCompleted()) {
      const timer = setTimeout(() => {
        setActive(true);
        setTimeout(() => setVisible(true), 100);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  // Update target rect when step changes
  useEffect(() => {
    if (!active) return;
    if (step.targetSelector) {
      const rect = getTargetRect(step.targetSelector);
      setTargetRect(rect);
    } else {
      setTargetRect(null);
    }
  }, [active, step, currentStep]);

  if (!mounted || !active) return null;

  const { card: cardStyle, arrowSide } = getTooltipStyle(step.position, targetRect);
  const isLast = currentStep === total - 1;
  const isFirst = currentStep === 0;

  const stepLabel = tr.stepOf
    .replace('{current}', String(currentStep + 1))
    .replace('{total}', String(total));

  return (
    <>
      {/* Inject keyframes for pulse animation */}
      <style>{`
        @keyframes tour-pulse-outer {
          0%, 100% { opacity: 0.4; transform: scale(1); }
          50% { opacity: 0.8; transform: scale(1.02); }
        }
        @keyframes tour-card-in {
          from { opacity: 0; transform: translateY(6px) scale(0.97); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes tour-card-out {
          from { opacity: 1; transform: translateY(0) scale(1); }
          to { opacity: 0; transform: translateY(-4px) scale(0.97); }
        }
      `}</style>

      {/* Very light backdrop */}
      <div
        className="fixed inset-0 z-[9990] pointer-events-none"
        style={{ background: 'rgba(0,0,0,0.12)' }}
      />

      {/* Spotlight cutout for target element */}
      {targetRect && (
        <div
          className="fixed inset-0 z-[9991] pointer-events-none"
          style={{
            background: `radial-gradient(ellipse ${targetRect.width + 60}px ${targetRect.height + 40}px at ${targetRect.left + targetRect.width / 2}px ${targetRect.top + targetRect.height / 2}px, transparent 0%, rgba(0,0,0,0.35) 100%)`,
          }}
        />
      )}

      {/* Pulse highlight around target element */}
      {targetRect && <PulseHighlight targetRect={targetRect} />}

      {/* Floating glass card */}
      <div
        className="fixed z-[9999] pointer-events-auto"
        style={{
          ...cardStyle,
          width: `${CARD_WIDTH}px`,
          animation: visible ? 'tour-card-in 0.25s ease-out forwards' : 'tour-card-out 0.2s ease-in forwards',
        }}
      >
        {/* Arrow pointing to target */}
        <div className="relative">
          <TooltipArrow side={arrowSide} />

          {/* Glass card body */}
          <div
            className="rounded-2xl border border-white/10 shadow-2xl shadow-black/40 overflow-hidden"
            style={{
              background: 'rgba(15, 15, 25, 0.97)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
            }}
          >
            {/* Top gradient bar */}
            <div className="h-0.5 w-full bg-gradient-to-r from-primary via-accent to-mint" />

            {/* Header */}
            <div className="flex items-center justify-between px-4 pt-3 pb-0">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center shrink-0">
                  {step.icon}
                </div>
                <span className="text-[10px] font-700 text-primary uppercase tracking-widest">{stepLabel}</span>
              </div>
              <button
                onClick={skipTour}
                className="p-1 rounded-lg hover:bg-white/10 text-white/40 hover:text-white/70 transition-all"
                title={tr.skip}
              >
                <X size={13} />
              </button>
            </div>

            {/* Content */}
            <div className="px-4 pt-3 pb-1">
              <h3 className="text-sm font-700 text-white mb-1.5 leading-snug">{stepData?.title}</h3>
              <p className="text-xs text-white/60 leading-relaxed">{stepData?.description}</p>
            </div>

            {/* Progress dots */}
            <div className="flex items-center justify-center gap-1 px-4 pt-3 flex-wrap">
              {TOUR_STEPS.map((_, i) => (
                <div
                  key={i}
                  className="rounded-full transition-all duration-300"
                  style={{
                    width: i === currentStep ? '14px' : '4px',
                    height: '4px',
                    background: i === currentStep
                      ? 'linear-gradient(90deg, #8b5cf6, #06b6d4)'
                      : i < currentStep
                        ? 'rgba(139,92,246,0.5)'
                        : 'rgba(255,255,255,0.15)',
                  }}
                />
              ))}
            </div>

            {/* Auto-dismiss bar */}
            <div className="px-4">
              <AutoDismissBar key={currentStep} duration={6000} onComplete={handleAutoDismiss} />
            </div>

            {/* Actions */}
            <div className="flex items-center justify-between px-4 py-3 mt-1">
              <button
                onClick={skipTour}
                className="text-[11px] text-white/35 hover:text-white/60 transition-colors font-500"
              >
                {tr.skip}
              </button>
              <div className="flex items-center gap-2">
                {!isFirst && (
                  <button
                    onClick={goBack}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-white/10 text-white/60 hover:text-white hover:border-white/20 text-[11px] font-600 transition-all hover:scale-105"
                  >
                    <ChevronLeft size={12} />
                    {tr.back}
                  </button>
                )}
                <button
                  onClick={goNext}
                  className="flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-[11px] font-700 transition-all hover:scale-105 active:scale-95"
                  style={{
                    background: 'linear-gradient(135deg, #8b5cf6, #06b6d4)',
                    color: 'white',
                    boxShadow: '0 2px 12px rgba(139,92,246,0.4)',
                  }}
                >
                  {isLast ? tr.finish : tr.next}
                  {isLast ? <CheckCircle size={11} /> : <ArrowRight size={11} />}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
